import React, { useState } from 'react';
import { KBBlock } from '../../../types';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

function getEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    // YouTube
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    if (u.hostname === 'youtu.be') {
      const v = u.pathname.slice(1);
      if (v) return `https://www.youtube.com/embed/${v}`;
    }
    // Vimeo
    if (u.hostname.includes('vimeo.com')) {
      const match = u.pathname.match(/\/(\d+)/);
      if (match) return `https://player.vimeo.com/video/${match[1]}`;
    }
    return null;
  } catch {
    return null;
  }
}

const VideoBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const url: string = block.data?.url ?? '';
  const caption: string = block.data?.caption ?? '';
  const [inputUrl, setInputUrl] = useState(url);

  if (isEditing) {
    return (
      <div className="space-y-3 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <input
            autoFocus
            type="url"
            value={inputUrl}
            placeholder="Paste YouTube or Vimeo URL…"
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onFinish(); }}
            className="flex-1 border border-gray-200 rounded px-3 py-1.5 text-sm outline-none focus:border-blue-400"
          />
        </div>
        <input
          type="text"
          value={caption}
          placeholder="Caption (optional)"
          onChange={(e) => onChange({ data: { ...block.data, caption: e.target.value } })}
          className="w-full border-b border-gray-200 pb-1 text-sm text-gray-500 outline-none placeholder-gray-300"
        />
        <div className="flex gap-2">
          <button
            onClick={() => { onChange({ data: { ...block.data, url: inputUrl } }); onFinish(); }}
            className="text-xs bg-blue-600 text-white px-3 py-1 rounded"
          >
            Embed
          </button>
          <button onClick={onFinish} className="text-xs text-gray-500 px-3 py-1 rounded border border-gray-200">Cancel</button>
        </div>
      </div>
    );
  }

  const embedUrl = getEmbedUrl(url);

  if (!url) {
    return (
      <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg border border-dashed border-gray-200">
        <span className="text-sm text-gray-300 italic">No video URL</span>
      </div>
    );
  }

  if (!embedUrl) {
    return (
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
        </svg>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">{url}</a>
      </div>
    );
  }

  return (
    <figure className="space-y-2">
      <div className="relative w-full rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
        <iframe
          src={embedUrl}
          title={caption || 'Video'}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {caption && <figcaption className="text-center text-xs text-gray-400 italic">{caption}</figcaption>}
    </figure>
  );
};

export default VideoBlock;
